import ConsentModel from '../model/consent'
import consentFactory from '../model/consent-factory'
import {isUndefined} from '../util/logic'
import CMPError from '../cmp-error'
import debug, {DEBUG, ERROR, WARN} from '../util/debug'
import {canUseCookies} from '../util/storage'

import API, {
  ConsentObject,
  CallbackData,
  VendorConsents,
  VendorConsentData,
  PingResponse,
  Command,
  SimpleCallback,
  GetVendorConsentCallback,
  GetConsentDataCallback,
  GetPublisherPurposesListCallback,
  GetVendorListCallback,
  GetNotRegisteredVendorsCallback,
  PingCallback,
  HasChoiceBeenMadeCallback,
  HasConsentedToAllCallback,
  HasStorageAccessCallback,
  ReturnValue,
  Callback,
  Parameter,
  QueueCommand,
  Queue
} from './api'

import {
  fetchNotRegisteredVendorList
} from '../service/vendor-list'

const requiresConsentModel = (target: BackendAPI, propertyKey: string, propertyDescriptor: PropertyDescriptor) => {
  const method = propertyDescriptor.value

  propertyDescriptor.value = function (this: BackendAPI, parameter: Parameter, callback: SimpleCallback) {
    if (!this.consentModel) {
      return callback(null, false)
    }

    method.call(this, parameter, callback)
  }
}

export default class BackendAPI extends API {
  public static async load (baseURL: string): Promise<BackendAPI> {
    const api = new this(baseURL)
    await api.init()
    return api
  }

  public consentModel?: ConsentModel
  protected isReady: boolean = false

  public async init (): Promise<void> {
    API.provision()
    this.consentModel = await consentFactory(this.baseURL, ConsentModel)
    this.isReady = true
    this.emit('ready')
  }

  public hasStorageAccess (_: undefined, callback: HasStorageAccessCallback) {
    callback(canUseCookies(), true)
  }

  @requiresConsentModel
  public getVendorConsents (vendorIds: undefined | string[], callback: GetVendorConsentCallback) {
    const ids = vendorIds && vendorIds.length
      ? vendorIds
      : this.consentModel!.getAllVendorIds()

    callback({
      gdprApplies: true,
      hasGlobalScope: false,
      metadata: this.consentModel!.daisybit,
      purposeConsents: this.consentModel!.vendorList.purposes.reduce(
        (consentObject, purpose) => ({
          ...consentObject,
          [purpose.id]: this.consentModel!.consented
        }),
        {}
      ),
      vendorConsents: ids.reduce(
        (consentObject: ConsentObject, id: string): ConsentObject => ({
          ...consentObject,
          [id]: this.consentModel!.consented
        }),
        {}
      )
    }, true)
  }

  @requiresConsentModel
  public getConsentData (consentStringVersion: string, callback: GetConsentDataCallback) {
    if (
      typeof consentStringVersion !== 'undefined' &&
      consentStringVersion !== null &&
      consentStringVersion !== this.consentModel!.getConsentVersion().toString()
    ) {
      callback(null, true)
    }

    callback({
      consentData: this.consentModel!.daisybit,
      gdprApplies: true,
      hasGlobalScope: false
    }, true)
  }

  public ping (_: null, callback: PingCallback) {
    callback({
      cmpLoaded: !!this.consentModel,
      gdprAppliesGlobally: false
    }, true)
  }

  /* MOL API */
  @requiresConsentModel
  public async consentToAll (origin: string, callback: SimpleCallback = () => {}) {
    this.consentModel!.consented = true

    try {
      await this.consentModel!.save(origin)
      callback(null, true)
    } catch (error) {
      callback(null, false)
    }
  }

  @requiresConsentModel
  public async dissentToAll (origin: string, callback: SimpleCallback = () => {}) {
    this.consentModel!.consented = false

    try {
      await this.consentModel!.save(origin)
      callback(null, true)
    } catch (error) {
      callback(null, false)
    }
  }

  @requiresConsentModel
  public onConsent (_: null, callback: SimpleCallback) {
    this.consentModel!.on('update', () => {
      if (this.consentModel!.consented) {
        callback(null, true)
      }
    })
  }

  @requiresConsentModel
  public onDissent (_: null, callback: SimpleCallback) {
    this.consentModel!.on('update', () => {
      if (!this.consentModel!.consented) {
        callback(null, true)
      }
    })
  }

  @requiresConsentModel
  public onUpdate (_: null, callback: HasConsentedToAllCallback) {
    this.consentModel!.on('update', () => {
      callback(this.consentModel!.consented, true)
    })
  }

  @requiresConsentModel
  public hasChoiceBeenMade (_: null, callback: HasChoiceBeenMadeCallback) {
    callback(this.consentModel!.hasChoiceBeenMade(), true)
  }

  @requiresConsentModel
  public hasConsentedToAll (_: null, callback: HasConsentedToAllCallback) {
    callback(this.consentModel!.consented, true)
  }

  @requiresConsentModel
  public ready (_: null, callback: SimpleCallback) {
    if (this.isReady) {
      callback(null, true)
    } else {
      this.on('ready', () => callback(null, true))
    }
  }

  @requiresConsentModel
  public reset (_: null, callback: SimpleCallback) {
    this.consentModel!.destroy()
    callback(null, true)
  }

  @requiresConsentModel
  public onReset (_: null, callback: SimpleCallback) {
    this.consentModel!.on('destroy', () => callback(null, true))
  }

  @requiresConsentModel
  public getVendorList (_: null, callback: GetVendorListCallback) {
    callback(this.consentModel!.vendorList, true)
  }

  public async getNotRegisteredVendorList (_: null, callback: GetNotRegisteredVendorsCallback) {
    const notRegisteredVendors = await fetchNotRegisteredVendorList(this.baseURL)
    callback(notRegisteredVendors, true)
  }

  @requiresConsentModel
  public getPublisherPurposesList (_: null, callback: GetPublisherPurposesListCallback) {
    callback(this.consentModel!.publisherPurposesList, true)
  }

  public call (command: Command, parameter: Parameter = null, callback: Callback = () => {}) {
    // @ts-ignore
    if (typeof this[command] !== 'function') {
      throw new Error(`"${command}" is not a function`)
    }

    // @ts-ignore
    this[command](parameter, callback)
  }
}
